<template>
  <div class="overflow-x-auto">
    <DataTable
      :value="rows"
      size="small"
      showGridlines
      scrollable
      dataKey="label"
      :rowClass="rowClass"
      :dt="COMPACT_TABLE_DT"
      class="text-sm"
      tableClass="min-w-[880px]"
      data-testid="txn-analytics-table"
    >
      <ColumnGroup type="header">
        <Row>
          <Column :header="groupHeader" :rowspan="2" />
          <Column :header="t('messages.transactions.analytics.table.n')" :rowspan="2" :pt="headerRightPT" />
          <Column
            :header="t('messages.transactions.analytics.table.area_mean_in', { unit: layout.areaUnit })"
            :rowspan="2"
            :pt="headerRightPT"
          />
          <Column
            :header="t('messages.transactions.analytics.table.sum_group')"
            :colspan="layout.sumSpan"
            :pt="headerCenterPT"
          />
          <Column
            v-if="layout.unitSpan > 0"
            :header="t('messages.transactions.analytics.table.unit_group_in', { unit: layout.priceUnit })"
            :colspan="layout.unitSpan"
            :pt="headerCenterPT"
          />
        </Row>
        <Row>
          <Column
            v-for="column in layout.groupedColumns"
            :key="column.field"
            :header="t(`messages.transactions.analytics.table.${column.leafKey}`)"
            :pt="headerRightPT"
          />
        </Row>
      </ColumnGroup>

      <Column field="label">
        <template #body="{ data }">
          <Skeleton v-if="loading" width="5rem" height="1rem" />
          <!-- The tag drops under the label rather than widen the column (RED-370). -->
          <div v-else class="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span class="whitespace-nowrap">{{ data.label }}</span>
            <Tag
              v-if="data.suppressed"
              severity="secondary"
              :value="t('messages.transactions.analytics.table.suppressed')"
              class="text-[0.65rem] font-normal whitespace-nowrap"
            />
          </div>
        </template>
      </Column>
      <Column field="count" :pt="cellRightPT">
        <template #body="{ data }">
          <Skeleton v-if="loading" width="3rem" height="1rem" class="ml-auto" />
          <span v-else class="tabular-nums">{{ formatEt(data.count, 0) }}</span>
        </template>
      </Column>
      <Column v-for="column in layout.valueColumns" :key="column.field" :field="column.field" :pt="cellRightPT">
        <template #body="{ data }">
          <Skeleton v-if="loading" width="4rem" height="1rem" class="ml-auto" />
          <span v-else class="tabular-nums">{{ formatCell(data, column.field, column.dp) }}</span>
        </template>
      </Column>

      <!-- Explicit key: v-if's auto key 0 makes PrimeVue's HelperSet re-add every body Column with a key-0 ancestor. -->
      <ColumnGroup v-if="showTotal" key="analytics-total-footer" type="footer">
        <Row>
          <Column :footer="total?.label ?? t('messages.transactions.analytics.table.kokku')" :pt="footerPT" />
          <Column :pt="footerRightPT">
            <template #footer>
              <Skeleton v-if="loading" width="3rem" height="1rem" class="ml-auto" />
              <span v-else class="tabular-nums">{{ formatEt(total?.count ?? 0, 0) }}</span>
            </template>
          </Column>
          <Column v-for="column in layout.valueColumns" :key="column.field" :pt="footerRightPT">
            <template #footer>
              <Skeleton v-if="loading" width="4rem" height="1rem" class="ml-auto" />
              <span v-else class="tabular-nums">{{ total ? formatCell(total, column.field, column.dp) : '–' }}</span>
            </template>
          </Column>
        </Row>
      </ColumnGroup>
    </DataTable>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { formatEt, formatCell } from '@/services/numberFormat'
import { COMPACT_TABLE_DT, tableLayout } from '@/services/analyticsTable'

/**
 * RED-310 — single-period price-statistics table (MaRu "Kinnisvara
 * hinnastatistika" clone). Renders one `periods[]` entry of the frozen
 * contract: the rows (area bands, or land purposes for RED-316) + a bold
 * KOKKU footer when the result's `table.total_row` says so. The first column
 * header is `table.group_header`; the value columns, their units and header
 * groups follow `columns` + `table` (tableLayout, RED-318: hectares and €/ha
 * for forest / arable land, no unit-price group for hoonestatud maa).
 * Suppressed rows (n < 5) carry null cells →
 * formatCell() renders "–" and the row gets the "alla 5 tehingu" tag. With
 * `loading` the same shell shows five skeleton rows so the layout does not
 * jump when the result lands.
 */
const props = defineProps({
  period: { type: Object, default: null },
  table: { type: Object, default: null },
  columns: { type: Array, default: null },
  loading: { type: Boolean, default: false },
})

const { t } = useI18n()

// Leaf value columns after Arv (dp = decimals shown) and the header groups.
const layout = computed(() => tableLayout(props.table, props.columns))

const SKELETON_ROWS = Array.from({ length: 5 }, (_, index) => ({ label: `skeleton-${index}`, skeleton: true }))

const groupHeader = computed(
  () => props.table?.group_header ?? t('messages.transactions.analytics.table.band'),
)
const showTotal = computed(() => props.table?.total_row !== false)

const rows = computed(() => (props.loading ? SKELETON_ROWS : (props.period?.bands ?? [])))
const total = computed(() => (props.loading ? null : (props.period?.total ?? null)))

const rowClass = data => (data.suppressed ? 'text-gray-400' : '')

const headerRightPT = { columnHeaderContent: { class: 'justify-end text-right' } }
const headerCenterPT = { columnHeaderContent: { class: 'justify-center' } }
const cellRightPT = { bodyCell: { class: 'text-right whitespace-nowrap' } }
const footerPT = { footerCell: { class: 'font-bold' } }
const footerRightPT = { footerCell: { class: 'font-bold text-right whitespace-nowrap' } }
</script>
